import { useEffect, useRef, useState } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  useTheme,
} from "@mui/material";
import MuiTooltipWrapper from "../Tooltip/Tooltip";

export default function CustomDropdownMenu({
  label,
  items = [],
  tooltip,
  placement = "bottom",
  isSelectedTab = false,
}) {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const prevOpen = useRef(open);

  const handleToggle = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen((prev) => !prev);
  };

  const handleClose = (event) => {
    if (anchorEl && event?.target && anchorEl.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleListKeyDown = (event) => {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorEl?.focus?.();
    }
    prevOpen.current = open;
  }, [open, anchorEl]);

  return (
    <div className="relative inline-block text-left">
      <MuiTooltipWrapper title={tooltip || ""} placement={placement}>
        <Button
          size="small"
          variant="text"
          onClick={handleToggle}
          endIcon={<ArrowDropDownIcon />}
          sx={{
            textTransform: "none",
            fontSize: { xs: "12px", sm: "14px", lg: "16px" },
            padding: { xs: "2px 4px", sm: "4px 8px", lg: 0 },
            minWidth: "auto",
            color: "inherit",
            fontWeight: isSelectedTab ? 600 : 400,
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
        >
          {label}
        </Button>
      </MuiTooltipWrapper>
      <Menu
        onClose={handleClose}
        open={open}
        anchorEl={anchorEl}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "visible",
              mt: 1.5,
              ml: { xs: 2, sm: 4 },
              border: "1px solid #D9D9D9",
              minWidth: { xs: "150px", sm: "180px" },
              "& .MuiAvatar-root": { width: 25, height: 25 },
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                border: "1px solid #D9D9D9",
                borderRight: 0,
                borderBottom: 0,
                top: 0,
                right: { xs: 15, sm: 27 },
                width: 20,
                height: 20,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
      >
        <MenuList
          autoFocusItem={open}
          id={`${label}-menu`}
          onKeyDown={handleListKeyDown}
        >
          {items.map((item, index) =>
            item.isSeparator ? (
              <hr
                key={`separator-${index}`}
                style={{
                  margin: "4px 0",
                  border: "none",
                  borderTop: "1px solid #E0E0E0",
                }}
              />
            ) : (
              <MuiTooltipWrapper
                key={item.label || index}
                title={item.tooltip}
                placement={item.placement}
              >
                <MenuItem
                  onClick={(event) => {
                    item.command?.(event);
                    handleClose(event);
                  }}
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    minHeight: { xs: "36px", sm: "48px" },
                    ...(item.selected
                      ? {
                          backgroundColor: theme.palette.primary.light,
                          color: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.light,
                          },
                          fontWeight: 500,
                        }
                      : {}),
                  }}
                >
                  {item.icon && (
                    <ListItemIcon sx={{ minWidth: { xs: 28, sm: 36 } }}>
                      {item.icon}
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      sx: { fontWeight: item.selected ? 600 : 400 },
                    }}
                  />
                </MenuItem>
              </MuiTooltipWrapper>
            )
          )}
        </MenuList>
      </Menu>
    </div>
  );
}
